'use client';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import WorryFreeDeliveryModal from '@/components/customercenter-ui/checkout/common/shippingInsurance/worryFreeDeliveryModal';
import ErrorIcon from '@/components/customercenter-ui/svgs/notifications/error';
import ToolTips from '@/components/customercenter-ui/tooltip';
import SalesTax from '@/components/customercenter-ui/tooltip/salesTax';
import SurChargeTooltip from '@/components/customercenter-ui/tooltip/surCharge';
import { useAccount } from '@/frontastic';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';
import { ErrorNotification } from '@/helpers/utils/notification';
import { formatCurrency, getAgentName } from '@/helpers/utils/siteUtils';
import { RELEASE_ORDER, CANCEL_TYPE } from '../../../../../../constants/constant';
import { CANCEL_ORDER_EVENT_TYPES, DEAFULT_ORDER_EVENT_MESSAGE } from '../../../cart/constants';
import { PAYLOAD_QUERY, RESPONSE_SUCCESS_MESSAGE } from '../../../customer-detail/constants';
import CancelOrderModal from '../../../modals/orderDetails/cancelOrder';
import HoldOrderModal from '../../../modals/orderDetails/holdOrder';
import ReleaseOrderModal from '../../../modals/orderDetails/releaseOrder';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { useToast } from '../../../ui/use-toast';
import { OrderSummaryInfo } from '../../types';
interface CustomerOrderSummary {
  Texts: any;
  isOrderReleasable: boolean;
  addNewNotes: any;
  customerId: string;
  isOrderCancelable: boolean;
  orderSummaryInfo: OrderSummaryInfo;
  getUpdatedData: () => void;
  isOrderHoldable: boolean;
  showActiveBrandSection?: boolean;
}
const CustomerOrderSummary = ({
  Texts,
  isOrderReleasable,
  addNewNotes,
  customerId,
  isOrderCancelable,
  getUpdatedData,
  orderSummaryInfo,
  isOrderHoldable,
  showActiveBrandSection = true,
}: CustomerOrderSummary) => {
  const { toast } = useToast();
  const { getOrderEvents } = useAccount();
  const params = useSearchParams();
  const [promoAmount, setPromoAmount] = useState('0');
  const isPromoCodeDisplayed = orderSummaryInfo?.promoCodeInfos ?? false;
  const [releaseSuccessMsg, setReleaseSuccessMsg] = useState(false);
  const [holdSuccessMsg, setHoldSuccessMsg] = useState(false);
  const [agentName] = useState(getAgentName().replace(/@.*/, ''));

  const handleHoldOrder = async (note: string) => {
    const payload = {
      query: {
        divisionCode: params.get(PAYLOAD_QUERY.DIVISION_CODE),
        orderId: params.get(PAYLOAD_QUERY.ORDER_ID),
      },
      data: {
        eventType: CANCEL_ORDER_EVENT_TYPES.HOLD_ORDER,
        createdBy: agentName,
        message: note,
      },
    } as any;
    await getOrderEvents(payload).then((response) => {
      if (response?.code === RESPONSE_SUCCESS_MESSAGE) {
        addNewNotes();
        getUpdatedData();
        setHoldSuccessMsg(true);
        setTimeout(() => {
          setHoldSuccessMsg(false);
        }, 10000);
      } else {
        ErrorNotification(toast, { description: response?.message, action: <ErrorIcon className="h-5 w-5" /> });
      }
    });
  };

  const releaseOrder = async () => {
    const payload = {
      query: {
        divisionCode: params.get(PAYLOAD_QUERY.DIVISION_CODE),
        orderId: params.get(PAYLOAD_QUERY.ORDER_ID),
      },
      data: {
        eventType: CANCEL_ORDER_EVENT_TYPES.RELEASE_ORDER,
        createdBy: agentName,
        message: DEAFULT_ORDER_EVENT_MESSAGE,
      },
    } as any;
    const response = await getOrderEvents(payload);
    if (response?.code === RESPONSE_SUCCESS_MESSAGE) {
      getUpdatedData();
      setReleaseSuccessMsg(true);
      setTimeout(() => {
        setReleaseSuccessMsg(false);
      }, 10000);
    }
  };

  const projectSettings: ProjectSettings = useProjSettings();

  const { allowedPermissionsForAgent } = projectSettings;

  const [isClientSide, setIsClientSide] = useState(false);

  useEffect(() => {
    setIsClientSide(true);
  }, []);

  useEffect(() => {
    if (orderSummaryInfo?.promoCodeInfos?.length > 0) {
      let promoAmount = 0;
      orderSummaryInfo?.promoCodeInfos?.forEach((promo: any) => {
        promoAmount += promo?.amount;
      });
      setPromoAmount(promoAmount?.toString());
    }
  }, [orderSummaryInfo]);

  return (
    <Card className="rounded-md border-[1px] border-foggyGray bg-lightSilver shadow-lg">
      <CardHeader className="px-5 py-3">
        <CardTitle className="pt-1.5 text-lg font-bold">{Texts.orderSummary}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col">
        <ul className="pb-3">
          <li className="mb-2 flex justify-between text-base font-normal">
            <p>
              {Texts.subtotal}({orderSummaryInfo?.numberOfItems} {Texts.items}):
            </p>
            <p>{formatCurrency(orderSummaryInfo?.subTotal?.toString() ?? '0')}</p>
          </li>
          {isPromoCodeDisplayed && (
            <li className="mb-2 flex justify-between text-base font-normal">
              <p>{Texts.promotions}:</p>
              <p className="text-red-600">-{formatCurrency(promoAmount)}</p>
            </li>
          )}
          {orderSummaryInfo?.returnInfo?.returnRefund ? (
            <li className="mb-2 flex justify-between text-base font-normal">
              <p>{Texts.adjustment}:</p>
              <p className="text-red-600">-{formatCurrency(orderSummaryInfo?.returnInfo?.returnRefund)}</p>
            </li>
          ) : (
            <></>
          )}
          <li className="mb-2 flex justify-between text-base font-normal">
            <p>{Texts.estimatedShipping}:</p>
            {orderSummaryInfo?.shipping === 'FREE' ? (
              <p className="text-red-600"> {formatCurrency('0')} </p>
            ) : (
              <p> {formatCurrency(orderSummaryInfo?.shipping?.toString() ?? '0')}</p>
            )}
          </li>
          <li className="mb-2 flex justify-between text-base font-normal">
            <section className="flex items-center">
              {Texts.shippingSurcharge}:{' '}
              <ToolTips
                className="ml-1 mt-1 h-4 w-4 text-[#B6B6B8] md:mt-2"
                Content={{ ContentToolTip: <SurChargeTooltip /> }}
              />
            </section>

            <p>{formatCurrency(orderSummaryInfo?.surcharge?.toString() ?? '0')}</p>
          </li>
          {(orderSummaryInfo?.insuranceInfo?.extendedPrice ?? 0) > 0 && (
            <section className="mt-2 flex justify-between">
              <section className="flex gap-1">
                <div className="pr-1">
                  <p>{mapLocaleToMeaningfulFormat().worryFreeDelivery}:</p>
                </div>
                <WorryFreeDeliveryModal />
              </section>
              <p>{formatCurrency(orderSummaryInfo?.insuranceInfo?.extendedPrice?.toString() ?? '0')}</p>
            </section>
          )}
          <li className="mb-2 flex justify-between text-base font-normal">
            <section className="flex items-center">
              {Texts.estimatedSalesTax}:{' '}
              <ToolTips
                className="ml-1 mt-1 h-4 w-4 text-[#B6B6B8] md:mt-2"
                Content={{ ContentToolTip: <SalesTax /> }}
              />
            </section>
            <p>{formatCurrency(orderSummaryInfo?.tax?.toString() ?? '0')}</p>
          </li>
          <li className="my-4 flex justify-between text-base font-bold">
            <p>{Texts.orderTotal}:</p>
            <p>{formatCurrency(orderSummaryInfo?.total?.toString() ?? '0')}</p>
          </li>
        </ul>
        {isPromoCodeDisplayed &&
          orderSummaryInfo?.promoCodeInfos?.map((promoCodes: any, index: any) => (
            <section key={index} className="flex justify-between border-t border-silverGray py-2 text-black">
              <p className="text-sm">
                {promoCodes?.promoCode === 'Promotional Discount' ? (
                  <>
                    <strong className="text-base text-[#DF2429]">{promoCodes?.promoCode}</strong> Applied to order
                  </>
                ) : (
                  <>
                    <strong className="text-base text-[#DF2429]">{promoCodes?.promoCode}</strong> Applied to order
                  </>
                )}
              </p>
            </section>
          ))}
        {isOrderCancelable && showActiveBrandSection ? (
          <>
            <div className="flex items-center justify-center gap-2 text-center text-base font-normal">
              {isOrderHoldable && (
                <>
                  <div className="text-blueLight underline">
                    <HoldOrderModal Texts={Texts} holdOrder={handleHoldOrder} />
                  </div>{' '}
                  <span className="mx-1">|</span>
                </>
              )}
              {allowedPermissionsForAgent?.includes(RELEASE_ORDER) &&
                isClientSide &&
                isOrderReleasable &&
                showActiveBrandSection && (
                  <>
                    <div className="text-blueLight underline">
                      <ReleaseOrderModal Texts={Texts} releaseOrder={releaseOrder} />{' '}
                    </div>{' '}
                    <span className="mx-1">|</span>
                  </>
                )}
              <div className="text-[#0075C9] underline">
                <CancelOrderModal
                  Texts={Texts}
                  cancelType={CANCEL_TYPE.ORDER}
                  CancelEvent={CANCEL_ORDER_EVENT_TYPES.ENTIRE_ORDER}
                  showOptions
                  className="underline"
                  comercetoolsOrder={false}
                  customerId={customerId}
                  getUpdatedData={getUpdatedData}
                />
              </div>
            </div>
          </>
        ) : (
          <section className="flex w-full items-center justify-center text-blueLight underline">
            {isOrderHoldable && <HoldOrderModal Texts={Texts} holdOrder={handleHoldOrder} />}
            {allowedPermissionsForAgent?.includes(RELEASE_ORDER) &&
              isClientSide &&
              isOrderReleasable &&
              showActiveBrandSection && <ReleaseOrderModal Texts={Texts} releaseOrder={releaseOrder} />}
          </section>
        )}
        {releaseSuccessMsg && (
          <p className="mt-3 text-center font-bold text-green-800">Order Hold Released Successfully</p>
        )}
        {holdSuccessMsg && <p className="mt-3 text-center font-bold text-green-800">Order Hold Successfully</p>}
      </CardContent>
    </Card>
  );
};

export default CustomerOrderSummary;
